import ContentState from "../../../Component/UI/ContentState";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { CenterView } from "../../../Component/style/common_style";
import { EmptyLiveState, LiveSectionTitle, LiveTabs, LiveTab } from "./Component/LiveListPresentation";
import LiveList from "./Component/LiveList";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import { LiveListItem } from "../../../Component/Interface/Live";
import api2 from "../../../Component/axios_Interceptor/api2";

interface UserLiveListProps {
  isLogin: boolean;
}

const UserLiveList = ({ isLogin }: UserLiveListProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState("공개");
  const [liveList, setLiveList] = useState<LiveListItem[]>([]);
  const [nextLiveList, setNextLiveList] = useState<LiveListItem[]>([]);

  const handleSelect = (option: string) => {
    setSelected(option);
  };

  const refreshLiveList = () => setReloadKey(value => value + 1);

  useEffect(() => {
    let active = true;
    let requestPending = false;
    const controller = new AbortController();
    const requestOptions = { signal: controller.signal, timeout: 15000 };
    setIsLoading(true);
    setError(false);
    setLiveList([]);
    setNextLiveList([]);

    const load = async (initial = false) => {
      if (requestPending) return;
      requestPending = true;
      try {
        // Guests have no followed broadcasts to request. Finish immediately.
        if (selected === "시크릿" && !isLogin) return;

        if (selected === "시크릿") {
          const { data } = await api.get(url.Communication_url.get_follow_live_list, requestOptions);
          if (!Array.isArray(data?.broadcasting) || !Array.isArray(data?.scheduled)) {
            throw new Error("Invalid followed live list response");
          }
          if (active) {
            setLiveList(data.broadcasting.filter((live: LiveListItem) => live.secret_yn === "y" && live.live_status === "방송중"));
            setNextLiveList(data.scheduled.filter((live: LiveListItem) => live.secret_yn === "y" && live.live_status === "예약"));
          }
        } else {
          const userResponse = isLogin
            ? await api.get(url.Communication_url.user_info, requestOptions)
            : null;
          if (!active) return;
          const { data } = await api2.get(url.Communication_url.get_main_live_list, {
            ...requestOptions,
            params: { userid: userResponse?.data.userid || "" },
          });
          if (!Array.isArray(data)) throw new Error("Invalid public live list response");
          if (active) {
            setLiveList(data.filter((live: LiveListItem) => live.secret_yn === "n" && live.live_status === "방송중"));
            setNextLiveList(data.filter((live: LiveListItem) => live.secret_yn === "n" && live.live_status === "예약"));
          }
        }
        if (active) setError(false);
      } catch {
        if (active && initial) setError(true);
      } finally {
        requestPending = false;
        if (active && initial) setIsLoading(false);
      }
    };

    void load(true);
    const interval = window.setInterval(() => {
      if (!document.hidden) void load();
    }, 15000);
    return () => {
      active = false;
      window.clearInterval(interval);
      controller.abort();
    };
  }, [selected, isLogin, reloadKey]);

  return (
    <Container>
      <LiveTabs role="tablist" aria-label="방송 구분">
        <LiveTab
          type="button" role="tab" id="home-public-live-tab" aria-controls="home-live-panel" aria-selected={selected === "공개"}
          $selected={selected === "공개"}
          onClick={() => {
            handleSelect("공개");
          }}
        >
          공개 라이브
        </LiveTab>
        <LiveTab
          type="button" role="tab" id="home-secret-live-tab" aria-controls="home-live-panel" aria-selected={selected === "시크릿"}
          $selected={selected === "시크릿"}
          onClick={() => {
            handleSelect("시크릿");
          }}
        >
          시크릿 라이브
        </LiveTab>
      </LiveTabs>

      <div role="tabpanel" id="home-live-panel" aria-labelledby={selected === "공개" ? "home-public-live-tab" : "home-secret-live-tab"}>
      <LiveSectionTitle />
      {isLoading ? (
        <Loading>
          <div className="loading" />
        </Loading>
      ) : error ? (
        <ContentState kind="error" icon="live" title="방송 목록을 불러오지 못했습니다." description="연결 상태를 확인한 뒤 다시 시도해 주세요." action={{ label: "다시 시도", onClick: refreshLiveList }} />
      ) : liveList.length > 0 ? (
        <LiveList
          liveList={liveList}
          isLive={true}
          search_live={refreshLiveList}
          isLogin={isLogin}
          follow={selected}
        />
      ) : (
        <EmptyLiveState />
      )}
      {selected === "시크릿" && (
        <>
          <LiveSectionTitle scheduled />
          {isLoading ? (
            <Loading>
              <div className="loading" />
            </Loading>
          ) : error ? (
            <NullDiv><NullText>라이브 예정 목록을 불러오지 못했습니다.</NullText></NullDiv>
          ) : nextLiveList.length > 0 ? (
            <LiveList
              liveList={nextLiveList}
              isLive={false}
              search_live={refreshLiveList}
              isLogin={isLogin}
              follow={selected}
            />
          ) : (
            <EmptyLiveState scheduled />
          )}
        </>
      )}
      {selected === "공개" && (
        <>
          <LiveSectionTitle scheduled />
          {isLoading ? (
            <Loading>
              <div className="loading" />
            </Loading>
          ) : error ? (
            <NullDiv><NullText>라이브 예정 목록을 불러오지 못했습니다.</NullText></NullDiv>
          ) : nextLiveList.length > 0 ? (
            <LiveList
              liveList={nextLiveList}
              isLive={false}
              search_live={refreshLiveList}
              isLogin={isLogin}
              follow={selected}
            />
          ) : (
            <EmptyLiveState scheduled />
          )}
        </>
      )}
      </div>
    </Container>
  );
};

export default UserLiveList;

const Container = styled.div`
  padding: 16px;
  margin-top: 18px;
`;

const NullDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 80px 0px 30px 0px;
`;

const NullText = styled.div`
  color: var(--user-text-muted);
  font-size: 14px;
  font-family: var(--font-Pretendard-Regular);
`;

const Loading = styled(CenterView)`
  padding: 100px 0px 50px 0px;
  color: var(--user-text-muted);
  div.loading {
    width: 30px;
    height: 30px;
    box-sizing: border-box;
    border: 3px solid #7955f0;
    border-top-color: var(--user-border);
    border-radius: 100%;
    animation: spin 1s ease-in-out infinite;
  }
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;
